/** Note utility functions for MIDI note numbers and frequency conversion. */

/** Convert a MIDI note number to frequency in Hz. A4 (note 69) = 440 Hz. */
export function noteToFreq(note: number): number {
	return 440 * 2 ** ((note - 69) / 12);
}

/**
 * Just intonation ratios for each chromatic interval (0–11 semitones above root).
 * Based on 5-limit just intonation, suitable for clean major/minor chords.
 */
const JI_RATIOS = [
	1 / 1, // 0  — unison
	16 / 15, // 1  — minor 2nd
	9 / 8, // 2  — major 2nd
	6 / 5, // 3  — minor 3rd
	5 / 4, // 4  — major 3rd
	4 / 3, // 5  — perfect 4th
	45 / 32, // 6  — tritone
	3 / 2, // 7  — perfect 5th
	8 / 5, // 8  — minor 6th
	5 / 3, // 9  — major 6th
	9 / 5, // 10 — minor 7th
	15 / 8, // 11 — major 7th
] as const;

/**
 * Calculate the cent adjustment to convert equal-temperament tuning to just intonation
 * for a given note relative to a root note.
 *
 * @param note  MIDI note number of the note to adjust.
 * @param root  MIDI note number of the root (lowest / first note in the chord).
 * @returns Cent offset to add to the note's ET frequency (can be negative).
 */
export function justIntonationCents(note: number, root: number): number {
	const semitones = note - root;
	if (semitones === 0) return 0;
	const chroma = ((semitones % 12) + 12) % 12;
	const ratio = JI_RATIOS[chroma] ?? 1;
	// JI cents for this chroma minus the equal-temperament cents
	return 1200 * Math.log2(ratio) - chroma * 100;
}

/** Note names in chromatic order. */
const NOTE_NAMES = [
	"C",
	"C#",
	"D",
	"D#",
	"E",
	"F",
	"F#",
	"G",
	"G#",
	"A",
	"A#",
	"B",
] as const;

/** Get the name of a MIDI note (e.g., "C4", "F#3"). */
export function noteName(note: number): string {
	const name = NOTE_NAMES[note % 12];
	const octave = Math.floor(note / 12) - 1;
	return `${name}${octave}`;
}

/** Check whether a MIDI note is a black key (sharp/flat). */
export function isBlackKey(note: number): boolean {
	const pc = note % 12;
	return pc === 1 || pc === 3 || pc === 6 || pc === 8 || pc === 10;
}

/** Get all MIDI note numbers for a given range of octaves. */
export function getNotesInRange(
	startNote: number,
	endNote: number,
): readonly number[] {
	const notes: number[] = [];
	for (let n = startNote; n <= endNote; n++) {
		notes.push(n);
	}
	return notes;
}
